import { Action, Reducer } from 'redux';
import { AppThunkAction } from '..';
import {
  NodeInfo,
  AssetInfo,
  NodeQueryResult,
} from '../../Interfaces/Assets/AssetInfo';
import { CategoryCountRequest } from '../../Interfaces/Assets/CategoryCountRequest';
import { CategoryCountResponseEntry } from '../../Interfaces/Assets/CategoryCountResponseEntry';
import {
  getRequestOptions,
  postRequestOptions,
} from '../../utils/utils';
import { FetchError } from '../../Interfaces/FetchError';
import { NodeTypeCounts } from '../../Interfaces/Assets/NodeTypeCounts';
import {
  SendToDivisionCommandInfo,
  SendToLanesCommandInfo,
  SendToNodeGroupsCommandInfo,
  SendToSiteNumbersCommandInfo,
} from '../../Interfaces/Commands/CommandInfo';
import { SendToDivisionsScheduledCommandInfo, SendToLanesScheduledCommandInfo, SendToNodeGroupsScheduledCommandInfo, SendToSiteNumbersScheduledCommandInfo } from '../../Interfaces/Commands/ScheduledCommandInfo';

export enum AssetActionTypes {
  RESET_STATE = 'RESET_STATE',
  UNAUTHORIZED_REQUEST = 'UNAUTHORIZED_REQUEST',
  RECEIVED_STORE_COUNT = 'RECEIVED_STORE_COUNT',
  RECEIVED_NODE_TYPE_COUNTS = 'RECEIVED_NODE_TYPE_COUNTS',

  REQUEST_CATEGORY_COUNT = 'REQUEST_CATEGORY_COUNT',
  RECEIVED_CATEGORY_COUNT = 'RECEIVED_CATEGORY_COUNT',

  REQUEST_SYSTEM_TYPE_COUNT = 'REQUEST_SYSTEM_TYPE_COUNT',
  RECEIVED_SYSTEM_TYPE_COUNT = 'RECEIVED_SYSTEM_TYPE_COUNT',

  REQUEST_NODE_ASSETS_INFO = 'REQUEST_NODE_ASSETS_INFO',
  RECEIVED_NODE_ASSETS_INFO = 'RECEIVED_NODE_ASSETS_INFO',
  EXPORT_FILTERED_ASSETS = 'EXPORT_FILTERED_ASSETS',
  REQUEST_ALL_NODES = 'REQUEST_ALL_NODES',
  RECEIVED_ALL_NODES = 'RECEIVED_ALL_NODES',
  REQUEST_ALL_ACTIVE_NODES = 'REQUEST_ALL_ACTIVE_NODES',
  RECEIVED_ALL_ACTIVE_NODES = 'RECEIVED_ALL_ACTIVE_NODES',
  REQUEST_NODES_BY_PAGE = 'REQUEST_NODES_BY_PAGE',
  RECEIVED_NODES_BY_PAGE = 'RECEIVED_NODES_BY_PAGE',
  RESET_NODES_RESULT = 'RESET_NODES_RESULT',
  RECEIVED_ASSET_STORE_LIST = 'RECEIVED_ASSET_STORE_LIST',
  REQUEST_ASSET_CATEGORY_LIST = ' REQUEST_ASSET_CATEGORY_LIST',
  RECEIVED_ASSET_CATEGORY_LIST = 'RECEIVED_ASSET_CATEGORY_LIST',
  REQUEST_ASSET_NAME_LIST = 'REQUEST_ASSET_NAME_LIST',
  RECEIVED_ASSET_NAME_LIST = 'RECEIVED_ASSET_NAME_LIST',
  RECEIVED_DIVISION_LIST = 'RECEIVED_DIVISION_LIST',
  RECEIVED_NODETYPE_LIST = 'RECEIVED_NODETYPE_LIST',
  RESET_FILTERED_ASSETS = 'RESET_FILTERED_ASSETS',
  RECEIVED_LANE_NUMBER_LIST = 'RECEIVED_LANE_NUMBER_LIST',
  RECEIVED_CLUSTER_LIST = 'RECEIVED_CLUSTER_LIST',
  RECEIVED_HTTP_RESPONSE_ERROR_CODE = 'RECEIVED_HTTP_RESPONSE_ERROR_CODE',
  RESET_HTTP_RESPONSE_FETCH_ERROR = 'RESET_HTTP_RESPONSE_FETCH_ERROR',
  RECEIVED_SITENUMBER_BY_DIVISION = 'RECEIVED_SITENUMBER_BY_DIVISION',
  RECEIVED_NODE_GROUP_BY_DIVISION_AND_SITENUMBER = 'RECEIVED_NODE_GROUP_BY_DIVISION_AND_SITENUMBER',
  RECEIVED_LANE_NUMBERS_BY_DIVISION_SITENUMBER_AND_CLUSTER = 'RECEIVED_LANE_NUMBERS_BY_DIVISION_SITENUMBER_AND_CLUSTER',
  NOTIFICATION_SEND_TO_DIVISION_SUCCESS_MESSAGE = 'NOTIFICATION_SEND_TO_DIVISION_SUCCESS_MESSAGE',
  NOTIFICATION_SEND_TO_SITE_NUMBER_SUCCESS_MESSAGE = 'NOTIFICATION_SEND_TO_SITE_NUMBER_SUCCESS_MESSAGE',
  NOTIFICATION_SEND_TO_NODE_GROUPS_SUCCESS_MESSAGE = 'NOTIFICATION_SEND_TO_NODE_GROUPS_SUCCESS_MESSAGE',
  NOTIFICATION_SEND_TO_LANES_SUCCESS_MESSAGE = 'NOTIFICATION_SEND_TO_LANES_SUCCESS_MESSAGE',
  REQUEST_REACTIVATE_NODE = 'REQUEST_REACTIVATE_NODE',
  RECEIVED_REACTIVATE_NODE = 'RECEIVED_REACTIVATE_NODE',
  REQUEST_UNREGISTER_NODE = 'REQUEST_UNREGISTER_NODE',
  RECEIVED_UNREGISTER_NODE = 'RECEIVED_UNREGISTER_NODE',
  COMMAND_SEND_SUCCESS_MESSAGE = 'COMMAND_SEND_SUCCESS_MESSAGE',

  RESET_ALL_SENT_COMMAND_RESULT = 'RESET_ALL_SENT_COMMAND_RESULT',
}
// -----------------
// STATE - This defines the type of data maintained in the Redux store.
export interface AssetsState {
  unauthorizedRequest: boolean;
  isFetchingNodes: boolean;
  storeCount: number;
  nodeTypeCounts: NodeTypeCounts;
  isFetchingCategoryCounts: boolean;
  categoryCounts: CategoryCountResponseEntry[];
  isFetchingNodeAssetsInfo: boolean;
  nodeAssetsInfo: AssetInfo[];
  filteredAssetsData: NodeInfo[];
  nodesByPage: NodeQueryResult;
  siteNumbers: string[];
  isFetchingAssetCategories: boolean;
  assetCategories: string[];
  isFetchingAssetNames: boolean;
  assetNames: Map<string, string[]>;
  divisions: string[];
  nodeTypes: string[];
  clusters: string[];
  laneNumbers: string[];
  assetValues: Map<CategoryCountRequest, string[]>;
  fetchError: FetchError;
  isFetchingCategoryCountsOverview: boolean;
  categoryCountsOverview: CategoryCountResponseEntry[];
  filteredTickertsData: NodeInfo[];
  siteNumberByDivision: string[];
  nodeGroupByDivisionAndSiteNumber: string[];
  laneNumberByDivisionSiteNumberAndCluster: string[];
  notificationSendToSiteNumberSuccessMessage: string;
  notificationSendToNodeGroupsSuccessMessage: string;
  notificationSendToLanesSuccessMessage: string;
  notificationSendToDivisionSuccessMessage: string;
  commandSuccessMessage: string;
}

// -----------------
// ACTIONS - These are serializable (hence replayable) descriptions of state transitions.
// They do not themselves have any side-effects; they just describe something that is going to happen.
interface ResetStateAction {
  type: AssetActionTypes.RESET_STATE;
}

interface UnauthorizedRequestAction {
  type: AssetActionTypes.UNAUTHORIZED_REQUEST;
}

interface ReceivedStoreCountAction {
  type: AssetActionTypes.RECEIVED_STORE_COUNT;
  count: number;
}

interface ReceivedNodeTypeCountsAction {
  type: AssetActionTypes.RECEIVED_NODE_TYPE_COUNTS;
  counts: NodeTypeCounts;
}

interface RequestCategoryCountAction {
  type: AssetActionTypes.REQUEST_CATEGORY_COUNT;
  refreshing: boolean;
}

interface ReceivedCategoryCountAction {
  type: AssetActionTypes.RECEIVED_CATEGORY_COUNT;
  counts: CategoryCountResponseEntry[];
  categoryCountRequest: CategoryCountRequest;
}

interface RequestSystemTypeCountAction {
  type: AssetActionTypes.REQUEST_SYSTEM_TYPE_COUNT;
  refreshing: boolean;
}

interface ReceivedSystemTypeCountAction {
  type: AssetActionTypes.RECEIVED_SYSTEM_TYPE_COUNT;
  counts: CategoryCountResponseEntry[];
  categoryCountRequest: CategoryCountRequest;
}

interface RequestNodeAssetsInfoAction {
  type: AssetActionTypes.REQUEST_NODE_ASSETS_INFO;
}

interface ReceivedNodeAssetsInfoAction {
  type: AssetActionTypes.RECEIVED_NODE_ASSETS_INFO;
  nodeId: number;
  nodeAssetsInfo: AssetInfo[];
}

interface ExportFilteredAssetsAction {
  type: AssetActionTypes.EXPORT_FILTERED_ASSETS;
  filteredNodesWithAsset: NodeInfo[];
}

interface RequestNodesByPageAction {
  type: AssetActionTypes.REQUEST_NODES_BY_PAGE;
}

interface ReceivedNodesByPageAction {
  type: AssetActionTypes.RECEIVED_NODES_BY_PAGE;
  nodesByPage: NodeQueryResult;
}

interface RequestAllNodesAction {
  type: AssetActionTypes.REQUEST_ALL_NODES;
}

interface ReceivedAllNodesAction {
  type: AssetActionTypes.RECEIVED_ALL_NODES;
  nodesByPage: NodeQueryResult;
}

interface ResetNodesByPageResultAction {
  type: AssetActionTypes.RESET_NODES_RESULT;
}

interface GetAssetStoreListAction {
  type: AssetActionTypes.RECEIVED_ASSET_STORE_LIST;
  siteNumbers: string[];
}

interface RequestAssetCategoryListAction {
  type: AssetActionTypes.REQUEST_ASSET_CATEGORY_LIST;
  refreshing: boolean;
}

interface GetAssetCategoryListAction {
  type: AssetActionTypes.RECEIVED_ASSET_CATEGORY_LIST;
  assetCategories: string[];
}

interface RequestAssetNameListAction {
  type: AssetActionTypes.REQUEST_ASSET_NAME_LIST;
  refreshing: boolean;
}

interface GetAssetNameListAction {
  type: AssetActionTypes.RECEIVED_ASSET_NAME_LIST;
  assetNames: string[];
  category: string;
}

interface GetDevisionListAction {
  type: AssetActionTypes.RECEIVED_DIVISION_LIST;
  divisions: string[];
}

interface GetNodeTypeListAction {
  type: AssetActionTypes.RECEIVED_NODETYPE_LIST;
  nodeTypes: string[];
}

interface ResetFilteredAssetsAction {
  type: AssetActionTypes.RESET_FILTERED_ASSETS;
}

interface GetLaneNumberListAction {
  type: AssetActionTypes.RECEIVED_LANE_NUMBER_LIST;
  laneNumbers: string[];
}

interface GetClusterListAction {
  type: AssetActionTypes.RECEIVED_CLUSTER_LIST;
  clusters: string[];
}

interface ReceivedErrorCode {
  type: AssetActionTypes.RECEIVED_HTTP_RESPONSE_ERROR_CODE;
  statusCode: number;
}

interface ResetHttpResponseError {
  type: AssetActionTypes.RESET_HTTP_RESPONSE_FETCH_ERROR;
}

interface GetSiteNumberByDivision {
  type: AssetActionTypes.RECEIVED_SITENUMBER_BY_DIVISION;
  siteNumberByDivision: string[];
}

interface GetNodeGroupByDivisionAndSiteNumber {
  type: AssetActionTypes.RECEIVED_NODE_GROUP_BY_DIVISION_AND_SITENUMBER;
  nodeGroupByDivisionAndSiteNumber: string[];
}

interface GetLaneNumberByDivisionSiteNumberAndCluster {
  type: AssetActionTypes.RECEIVED_LANE_NUMBERS_BY_DIVISION_SITENUMBER_AND_CLUSTER;
  laneNumberByDivisionSiteNumberAndCluster: string[];
}

interface NotificationSendToSiteNumberSuccessMessage {
  type: AssetActionTypes.NOTIFICATION_SEND_TO_SITE_NUMBER_SUCCESS_MESSAGE;
  message: string;
}

interface NotificationSendToNodeGroupsSuccessMessage {
  type: AssetActionTypes.NOTIFICATION_SEND_TO_NODE_GROUPS_SUCCESS_MESSAGE;
  message: string;
}

interface NotificationSendToLanesSuccessMessage {
  type: AssetActionTypes.NOTIFICATION_SEND_TO_LANES_SUCCESS_MESSAGE;
  message: string;
}

interface NotificationSendToDivisionSuccessMessage {
  type: AssetActionTypes.NOTIFICATION_SEND_TO_DIVISION_SUCCESS_MESSAGE;
  message: string;
}

interface ResetAllSentCommandResult {
  type: AssetActionTypes.RESET_ALL_SENT_COMMAND_RESULT;
}

interface CommandSendSuccessMessage {
  type: AssetActionTypes.COMMAND_SEND_SUCCESS_MESSAGE;
  message: string;
}

// Declare a 'discriminated union' type. This guarantees that all references to 'type' properties contain one of the
// declared type strings (and not any other arbitrary string).
type KnownAction =
  | ResetStateAction
  | UnauthorizedRequestAction
  | ReceivedStoreCountAction
  | ReceivedNodeTypeCountsAction
  | RequestCategoryCountAction
  | ReceivedCategoryCountAction
  | RequestSystemTypeCountAction
  | ReceivedSystemTypeCountAction
  | RequestNodeAssetsInfoAction
  | ReceivedNodeAssetsInfoAction
  | ExportFilteredAssetsAction
  | RequestNodesByPageAction
  | ReceivedNodesByPageAction
  | RequestAllNodesAction
  | ReceivedAllNodesAction
  | ResetNodesByPageResultAction
  | GetAssetStoreListAction
  | RequestAssetCategoryListAction
  | GetAssetCategoryListAction
  | RequestAssetNameListAction
  | GetAssetNameListAction
  | GetDevisionListAction
  | GetNodeTypeListAction
  | ResetFilteredAssetsAction
  | GetLaneNumberListAction
  | GetClusterListAction
  | ReceivedErrorCode
  | ResetHttpResponseError
  | GetSiteNumberByDivision
  | GetNodeGroupByDivisionAndSiteNumber
  | GetLaneNumberByDivisionSiteNumberAndCluster
  | NotificationSendToSiteNumberSuccessMessage
  | NotificationSendToNodeGroupsSuccessMessage
  | NotificationSendToLanesSuccessMessage
  | NotificationSendToDivisionSuccessMessage
  | ResetAllSentCommandResult
  | CommandSendSuccessMessage;

// ----------------
// ACTION CREATORS - These are functions exposed to UI components that will trigger a state transition.
// They don't directly mutate state, but they can have external side-effects (such as loading data).
const isResponseOk = (response: Response, dispatch: any): boolean => {
  if (response.ok) return true;
  if (response.status === 401) {
    dispatch({
      type: AssetActionTypes.UNAUTHORIZED_REQUEST,
    });
  } else {
    dispatch({
      type: AssetActionTypes.RECEIVED_HTTP_RESPONSE_ERROR_CODE,
      statusCode: response.status,
    });
  }
  return false;
};

const fetchCategoryCounts = async (
  token: string | undefined,
  categoryCountRequest: CategoryCountRequest
): Promise<any> => {
  const response = await fetch(
    `assets/GetCategoryCount`,
    postRequestOptions(token, categoryCountRequest)
  ).catch(error => {
    console.error(error);
    return Promise.reject(error);
  });
  return response;
};

export const actionCreators = {
  resetState: (): AppThunkAction<KnownAction> => async (dispatch, getState) => {
    const appState = getState();
    if (!appState) return;

    console.log(`Dispatching ${AssetActionTypes.RESET_STATE}`);

    dispatch({
      type: AssetActionTypes.RESET_STATE,
    });
  },

  fetchStoreCount:
    (): AppThunkAction<KnownAction> => async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      const response = await fetch(
        `assets/GetStoreCount`,
        getRequestOptions(appState.userSessionReducer.sessionInfo?.Token)
      ).catch(error => {
        console.error(error);
        return Promise.reject(error);
      });

      if (!isResponseOk(response, dispatch)) return;

      const result = (await response.json()) as number;

      dispatch({
        type: AssetActionTypes.RECEIVED_STORE_COUNT,
        count: result,
      });
    },

  fetchNodeCount:
    (): AppThunkAction<KnownAction> => async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      const response = await fetch(
        `assets/GetNodeCount`,
        getRequestOptions(appState.userSessionReducer.sessionInfo?.Token)
      ).catch(error => {
        console.error(error);
        return Promise.reject(error);
      });

      if (!isResponseOk(response, dispatch)) return;

      const result = (await response.json()) as number;
    },

  fetchActiveNodeTypeCounts:
    (): AppThunkAction<KnownAction> => async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      const response = await fetch(
        `assets/GetActiveNodeTypeCounts`,
        getRequestOptions(appState.userSessionReducer.sessionInfo?.Token)
      ).catch(error => {
        console.error(error);
        return Promise.reject(error);
      });

      if (!isResponseOk(response, dispatch)) return;

      const result = (await response.json()) as NodeTypeCounts;

      dispatch({
        type: AssetActionTypes.RECEIVED_NODE_TYPE_COUNTS,
        counts: result,
      });
    },

  fetchCategoryCounts:
    (categoryCountRequest: CategoryCountRequest): AppThunkAction<KnownAction> =>
    async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      dispatch({
        type: AssetActionTypes.REQUEST_CATEGORY_COUNT,
        refreshing: true,
      });

      var result: CategoryCountResponseEntry[] = [];

      const response = await fetchCategoryCounts(
        appState.userSessionReducer.sessionInfo?.Token,
        categoryCountRequest
      );

      if (!isResponseOk(response, dispatch)) return;

      result = (await response.json()) as CategoryCountResponseEntry[];

      console.log(`Dispatching ${AssetActionTypes.RECEIVED_CATEGORY_COUNT}`);

      dispatch({
        type: AssetActionTypes.RECEIVED_CATEGORY_COUNT,
        counts: result,
        categoryCountRequest: categoryCountRequest,
      });
    },

  fetchSystemTypes:
    (): AppThunkAction<KnownAction> => async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      console.log(`Dispatching ${AssetActionTypes.REQUEST_SYSTEM_TYPE_COUNT}`);

      dispatch({
        type: AssetActionTypes.REQUEST_SYSTEM_TYPE_COUNT,
        refreshing: true,
      });

      let categoryCountRequest: CategoryCountRequest = {
        Category: 'U-Scan',
        Name: 'SystemType',
      };

      let result: CategoryCountResponseEntry[] = [];

      const response = await fetchCategoryCounts(
        appState.userSessionReducer.sessionInfo?.Token,
        categoryCountRequest
      );

      if (!isResponseOk(response, dispatch)) return;

      result = (await response.json()) as CategoryCountResponseEntry[];

      if (result.length < 1) {
        console.log('Fetching CasingType (SystemType asset not found)');
        categoryCountRequest.Name = 'CasingType';
        const response = await fetchCategoryCounts(
          appState.userSessionReducer.sessionInfo?.Token,
          categoryCountRequest
        );

        if (!isResponseOk(response, dispatch)) return;

        result = (await response.json()) as CategoryCountResponseEntry[];
      }

      console.log(`Dispatching ${AssetActionTypes.RECEIVED_SYSTEM_TYPE_COUNT}`);

      dispatch({
        type: AssetActionTypes.RECEIVED_SYSTEM_TYPE_COUNT,
        counts: result,
        categoryCountRequest: categoryCountRequest,
      });
    },

  getNodeAssetsInfo:
    (nodeId: number): AppThunkAction<KnownAction> =>
    async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      console.log(`Dispatching ${AssetActionTypes.REQUEST_NODE_ASSETS_INFO}`);
      dispatch({
        type: AssetActionTypes.REQUEST_NODE_ASSETS_INFO,
      });

      const response = await fetch(
        `assets/GetAllForNode/${nodeId}`,
        getRequestOptions(appState.userSessionReducer.sessionInfo?.Token)
      ).catch(error => {
        console.error(error);
        return Promise.reject(error);
      });

      if (!isResponseOk(response, dispatch)) return;

      const result = (await response.json()) as AssetInfo[];

      console.log(`Dispatching ${AssetActionTypes.RECEIVED_NODE_ASSETS_INFO}`);

      dispatch({
        type: AssetActionTypes.RECEIVED_NODE_ASSETS_INFO,
        nodeId: nodeId,
        nodeAssetsInfo: result,
      });
    },

  exportFilteredAssets:
    (): AppThunkAction<KnownAction> => async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      const response = await fetch(
        `assets/GetFilteredNodesWithAssets`,
        postRequestOptions(
          appState.userSessionReducer.sessionInfo?.Token,
          appState.userSettingsReducer.nodesFilters
        )
      ).catch(error => {
        console.error(error);
        return Promise.reject(error);
      });

      if (!isResponseOk(response, dispatch)) return;

      const result = (await response.json()) as NodeInfo[];

      console.log(`Dispatching ${AssetActionTypes.EXPORT_FILTERED_ASSETS}`);

      dispatch({
        type: AssetActionTypes.EXPORT_FILTERED_ASSETS,
        filteredNodesWithAsset: result,
      });
    },

  getNodesByPage:
    (includeDecommissioned: boolean): AppThunkAction<KnownAction> =>
    async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      console.log(`Dispatching ${AssetActionTypes.REQUEST_NODES_BY_PAGE}`);
      dispatch({
        type: AssetActionTypes.REQUEST_NODES_BY_PAGE,
      });

      let skip = appState.userSettingsReducer.nodesGridDataState.skip as number;
      let pageSize = appState.userSettingsReducer.nodesGridDataState
        .take as number;

      let currentNodesLength = appState.assetsReducer.nodesByPage.Result.length;

      let pageNumber = Math.floor(skip / pageSize) + 1;

      if (currentNodesLength === 0 || skip > currentNodesLength) {
        if (currentNodesLength > 1) {
          dispatch({
            type: AssetActionTypes.RESET_NODES_RESULT,
          });
        }
        pageSize = pageNumber * pageSize;
        pageNumber = 1;
      }

      const nodeQueryInfo = {
        PageNumber: pageNumber,
        PageSize: pageSize,
        NodeFilters: appState.userSettingsReducer.nodesFilters,
      };

      const response = await fetch(
        `assets/GetAllByPage/${includeDecommissioned}`,
        postRequestOptions(
          appState.userSessionReducer.sessionInfo?.Token,
          nodeQueryInfo
        )
      ).catch(error => {
        console.error(error);
        return Promise.reject(error);
      });

      if (!isResponseOk(response, dispatch)) return;

      const result = (await response.json()) as NodeQueryResult;

      dispatch({
        type: AssetActionTypes.RECEIVED_NODES_BY_PAGE,
        nodesByPage: result,
      });
    },

  getAllNodes:
    (includeDecommissioned: boolean): AppThunkAction<KnownAction> =>
    async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      if (includeDecommissioned) {
        console.log(`Dispatching ${AssetActionTypes.REQUEST_ALL_NODES}`);
      } else {
        console.log(`Dispatching ${AssetActionTypes.REQUEST_ALL_ACTIVE_NODES}`);
      }
      dispatch({
        type: AssetActionTypes.REQUEST_ALL_NODES,
      });

      let skip = appState.userSettingsReducer.nodesGridDataState.skip as number;
      let pageSize = appState.userSettingsReducer.nodesGridDataState
        .take as number;

      let currentNodesLength = appState.assetsReducer.nodesByPage.Result.length;

      let pageNumber = Math.floor(skip / pageSize) + 1;

      if (currentNodesLength === 0 || skip > currentNodesLength) {
        if (currentNodesLength > 1) {
          dispatch({
            type: AssetActionTypes.RESET_NODES_RESULT,
          });
        }
        pageSize = pageNumber * pageSize;
        pageNumber = 1;
      }

      const nodeQueryInfo = {
        PageNumber: pageNumber,
        PageSize: pageSize,
        NodeFilters: appState.userSettingsReducer.nodesFilters,
      };

      const response = await fetch(
        `assets/GetAllByPage/${includeDecommissioned}`,
        postRequestOptions(
          appState.userSessionReducer.sessionInfo?.Token,
          nodeQueryInfo
        )
      ).catch(error => {
        console.error(error);
        return Promise.reject(error);
      });

      if (!isResponseOk(response, dispatch)) return;

      const result = (await response.json()) as NodeQueryResult;

      if (includeDecommissioned) {
        console.log(`Dispatching ${AssetActionTypes.RECEIVED_ALL_NODES}`);
      } else {
        console.log(
          `Dispatching ${AssetActionTypes.RECEIVED_ALL_ACTIVE_NODES}`
        );
      }

      dispatch({
        type: AssetActionTypes.RECEIVED_ALL_NODES,
        nodesByPage: result,
      });
    },

  reactivateNode:
    (uuid: string): AppThunkAction<KnownAction> =>
    async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      const response = await fetch(
        `assets/Reactivate`,
        postRequestOptions(appState.userSessionReducer.sessionInfo?.Token, uuid)
      ).catch(error => {
        console.error(error);
        return Promise.reject(error);
      });

      if (!isResponseOk(response, dispatch)) return;

      console.log(`Dispatching ${AssetActionTypes.RECEIVED_REACTIVATE_NODE}`);
    },

  unregisterNode:
    (uuid: string): AppThunkAction<KnownAction> =>
    async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      const response = await fetch(
        `assets/Unregister`,
        postRequestOptions(appState.userSessionReducer.sessionInfo?.Token, uuid)
      ).catch(error => {
        console.error(error);
        return Promise.reject(error);
      });

      if (!isResponseOk(response, dispatch)) return;

      console.log(`Dispatching ${AssetActionTypes.RECEIVED_UNREGISTER_NODE}`);
    },

  resetNodesResult:
    (): AppThunkAction<KnownAction> => async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      console.log(`Dispatching ${AssetActionTypes.RESET_NODES_RESULT}`);

      dispatch({
        type: AssetActionTypes.RESET_NODES_RESULT,
      });
    },

  getAssetStoreList:
    (): AppThunkAction<KnownAction> => async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      const response = await fetch(
        `assets/GetSiteNumbers`,
        getRequestOptions(appState.userSessionReducer.sessionInfo?.Token)
      ).catch(error => {
        console.error(error);
        return Promise.reject(error);
      });

      if (!isResponseOk(response, dispatch)) return;

      const result = (await response.json()) as string[];

      console.log(`Dispatching ${AssetActionTypes.RECEIVED_ASSET_STORE_LIST}`);

      dispatch({
        type: AssetActionTypes.RECEIVED_ASSET_STORE_LIST,
        siteNumbers: result,
      });
    },

  getAssetCategoryList:
    (): AppThunkAction<KnownAction> => async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      dispatch({
        type: AssetActionTypes.REQUEST_ASSET_CATEGORY_LIST,
        refreshing: true,
      });

      const response = await fetch(
        `assets/GetAssetCategories`,
        getRequestOptions(appState.userSessionReducer.sessionInfo?.Token)
      ).catch(error => {
        console.error(error);
        return Promise.reject(error);
      });

      if (!isResponseOk(response, dispatch)) return;

      const result = (await response.json()) as string[];

      console.log(
        `Dispatching ${AssetActionTypes.RECEIVED_ASSET_CATEGORY_LIST}`
      );

      result.sort();

      dispatch({
        type: AssetActionTypes.RECEIVED_ASSET_CATEGORY_LIST,
        assetCategories: result,
      });
    },

  getAssetNameList:
    (assetCategory: string): AppThunkAction<KnownAction> =>
    async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      dispatch({
        type: AssetActionTypes.REQUEST_ASSET_NAME_LIST,
        refreshing: true,
      });

      const response = await fetch(
        `assets/GetAssetNames/${assetCategory}`,
        getRequestOptions(appState.userSessionReducer.sessionInfo?.Token)
      ).catch(error => {
        console.error(error);
        return Promise.reject(error);
      });

      if (!isResponseOk(response, dispatch)) return;

      const result = (await response.json()) as string[];

      console.log(`Dispatching ${AssetActionTypes.RECEIVED_ASSET_NAME_LIST}`);

      dispatch({
        type: AssetActionTypes.RECEIVED_ASSET_NAME_LIST,
        assetNames: result,
        category: assetCategory,
      });
    },

  getSiteNumberByDivision:
    (division: string): AppThunkAction<KnownAction> =>
    async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      const response = await fetch(
        `assets/GetSiteNumbersByDivision/${division}`,
        getRequestOptions(appState.userSessionReducer.sessionInfo?.Token)
      ).catch(error => {
        console.error(error);
        return Promise.reject(error);
      });

      if (!isResponseOk(response, dispatch)) return;

      const result = (await response.json()) as string[];

      console.log(
        `Dispatching ${AssetActionTypes.RECEIVED_SITENUMBER_BY_DIVISION}`
      );

      dispatch({
        type: AssetActionTypes.RECEIVED_SITENUMBER_BY_DIVISION,
        siteNumberByDivision: result,
      });
    },

  getNodeGroupByDivisionAndSiteNumber:
    (division: string, sitenumber: string): AppThunkAction<KnownAction> =>
    async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      const response = await fetch(
        `assets/GetNodeGroupsByDivisionAndSiteNumber/${division}/${sitenumber}`,
        getRequestOptions(appState.userSessionReducer.sessionInfo?.Token)
      ).catch(error => {
        console.error(error);
        return Promise.reject(error);
      });

      if (!isResponseOk(response, dispatch)) return;

      const result = (await response.json()) as string[];

      console.log(
        `Dispatching ${AssetActionTypes.RECEIVED_NODE_GROUP_BY_DIVISION_AND_SITENUMBER}`
      );

      dispatch({
        type: AssetActionTypes.RECEIVED_NODE_GROUP_BY_DIVISION_AND_SITENUMBER,
        nodeGroupByDivisionAndSiteNumber: result,
      });
    },

  getLaneNumberByDivisionSiteNumberAndCluster:
    (
      division: string,
      siteNumber: string,
      cluster: string
    ): AppThunkAction<KnownAction> =>
    async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      const response = await fetch(
        `assets/GetLaneNumbersByDivisionStoreAndCluster/${division}/${siteNumber}/${cluster}`,
        getRequestOptions(appState.userSessionReducer.sessionInfo?.Token)
      ).catch(error => {
        console.error(error);
        return Promise.reject(error);
      });

      if (!isResponseOk(response, dispatch)) return;

      const result = (await response.json()) as string[];

      console.log(
        `Dispatching ${AssetActionTypes.RECEIVED_LANE_NUMBERS_BY_DIVISION_SITENUMBER_AND_CLUSTER}`
      );

      dispatch({
        type: AssetActionTypes.RECEIVED_LANE_NUMBERS_BY_DIVISION_SITENUMBER_AND_CLUSTER,
        laneNumberByDivisionSiteNumberAndCluster: result,
      });
    },

  getDivisionList:
    (): AppThunkAction<KnownAction> => async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      const response = await fetch(
        `assets/GetDivisions`,
        getRequestOptions(appState.userSessionReducer.sessionInfo?.Token)
      ).catch(error => {
        console.error(error);
        return Promise.reject(error);
      });

      if (!isResponseOk(response, dispatch)) return;

      const result = (await response.json()) as string[];

      console.log(`Dispatching ${AssetActionTypes.RECEIVED_DIVISION_LIST}`);

      dispatch({
        type: AssetActionTypes.RECEIVED_DIVISION_LIST,
        divisions: result,
      });
    },

  getNodeTypeList:
    (): AppThunkAction<KnownAction> => async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      const response = await fetch(
        `assets/GetNodeTypes`,
        getRequestOptions(appState.userSessionReducer.sessionInfo?.Token)
      ).catch(error => {
        console.error(error);
        return Promise.reject(error);
      });

      if (!isResponseOk(response, dispatch)) return;

      const result = (await response.json()) as string[];

      console.log(`Dispatching ${AssetActionTypes.RECEIVED_NODETYPE_LIST}`);

      dispatch({
        type: AssetActionTypes.RECEIVED_NODETYPE_LIST,
        nodeTypes: result,
      });
    },

  getLaneNumbers:
    (): AppThunkAction<KnownAction> => async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      const response = await fetch(
        `assets/GetLaneNumbers`,
        getRequestOptions(appState.userSessionReducer.sessionInfo?.Token)
      ).catch(error => {
        console.error(error);
        return Promise.reject(error);
      });

      if (!isResponseOk(response, dispatch)) return;

      const result = (await response.json()) as string[];

      console.log(`Dispatching ${AssetActionTypes.RECEIVED_LANE_NUMBER_LIST}`);

      dispatch({
        type: AssetActionTypes.RECEIVED_LANE_NUMBER_LIST,
        laneNumbers: result,
      });
    },

  getNodeGroups:
    (): AppThunkAction<KnownAction> => async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      const response = await fetch(
        `assets/GetNodeGroups`,
        getRequestOptions(appState.userSessionReducer.sessionInfo?.Token)
      ).catch(error => {
        console.error(error);
        return Promise.reject(error);
      });

      if (!isResponseOk(response, dispatch)) return;

      const result = (await response.json()) as string[];

      console.log(`Dispatching ${AssetActionTypes.RECEIVED_CLUSTER_LIST}`);

      dispatch({
        type: AssetActionTypes.RECEIVED_CLUSTER_LIST,
        clusters: result,
      });
    },

  resetFilteredAssets:
    (): AppThunkAction<KnownAction> => async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      console.log(`Dispatching ${AssetActionTypes.RESET_FILTERED_ASSETS}`);

      dispatch({
        type: AssetActionTypes.RESET_FILTERED_ASSETS,
      });
    },

  resetFetchError:
    (): AppThunkAction<KnownAction> => async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      console.log(
        `Dispatching ${AssetActionTypes.RESET_HTTP_RESPONSE_FETCH_ERROR}`
      );

      dispatch({
        type: AssetActionTypes.RESET_HTTP_RESPONSE_FETCH_ERROR,
      });
    },

  sendToSiteNumberCommand:
    (
      sendToSiteNumberCommandInfo: SendToSiteNumbersCommandInfo
    ): AppThunkAction<KnownAction> =>
    async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      dispatch({
        type: AssetActionTypes.RESET_ALL_SENT_COMMAND_RESULT,
      });

      const response = await fetch(
        `CommandResponse/SendToSiteNumberCommand`,
        postRequestOptions(
          appState.userSessionReducer.sessionInfo?.Token,
          sendToSiteNumberCommandInfo
        )
      ).catch(error => {
        console.error(error);
        return Promise.reject(error);
      });

      if (!isResponseOk(response, dispatch)) return;

      const result = (await response.json()) as { message: string };

      console.log(
        `Dispatched command ${sendToSiteNumberCommandInfo.CommandInfo.Command} to:
        Stores:`,
        sendToSiteNumberCommandInfo.SiteNumbers
      );

      dispatch({
        type: AssetActionTypes.NOTIFICATION_SEND_TO_SITE_NUMBER_SUCCESS_MESSAGE,
        message: result.message,
      });
    },

  sendToNodeGroupsCommand:
    (
      sendToNodeGroupsCommandInfo: SendToNodeGroupsCommandInfo
    ): AppThunkAction<KnownAction> =>
    async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      dispatch({
        type: AssetActionTypes.RESET_ALL_SENT_COMMAND_RESULT,
      });

      const response = await fetch(
        `CommandResponse/SendToNodeGroupsCommand`,
        postRequestOptions(
          appState.userSessionReducer.sessionInfo?.Token,
          sendToNodeGroupsCommandInfo
        )
      ).catch(error => {
        console.error(error);
        return Promise.reject(error);
      });

      if (!isResponseOk(response, dispatch)) return;

      const result = (await response.json()) as { message: string };

      console.log(
        `Dispatched command ${sendToNodeGroupsCommandInfo.CommandInfo.Command} to:
        Store: ${sendToNodeGroupsCommandInfo.SiteNumber}
        Clusters:`,
        sendToNodeGroupsCommandInfo.NodeGroups
      );

      dispatch({
        type: AssetActionTypes.NOTIFICATION_SEND_TO_NODE_GROUPS_SUCCESS_MESSAGE,
        message: result.message,
      });
    },

  sendToLanesCommand:
    (
      sendToLanesCommandInfo: SendToLanesCommandInfo
    ): AppThunkAction<KnownAction> =>
    async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      dispatch({
        type: AssetActionTypes.RESET_ALL_SENT_COMMAND_RESULT,
      });

      const response = await fetch(
        `CommandResponse/SendToLanesCommand`,
        postRequestOptions(
          appState.userSessionReducer.sessionInfo?.Token,
          sendToLanesCommandInfo
        )
      ).catch(error => {
        console.error(error);
        return Promise.reject(error);
      });

      if (!isResponseOk(response, dispatch)) return;

      const result = (await response.json()) as { message: string };

      console.log(
        `Dispatched command ${sendToLanesCommandInfo.CommandInfo.Command} to:
        Store: ${sendToLanesCommandInfo.SiteNumber}
        Cluster: ${sendToLanesCommandInfo.NodeGroup}
        Lanes: `,
        sendToLanesCommandInfo.LaneNumbers
      );

      dispatch({
        type: AssetActionTypes.NOTIFICATION_SEND_TO_LANES_SUCCESS_MESSAGE,
        message: result.message,
      });
    },

  sendToDivisionCommand:
    (
      sendToDivisionCommandInfo: SendToDivisionCommandInfo
    ): AppThunkAction<KnownAction> =>
    async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      dispatch({
        type: AssetActionTypes.RESET_ALL_SENT_COMMAND_RESULT,
      });

      const response = await fetch(
        `CommandResponse/SendToDivisionCommand`,
        postRequestOptions(
          appState.userSessionReducer.sessionInfo?.Token,
          sendToDivisionCommandInfo
        )
      ).catch(error => {
        console.error(error);
        return Promise.reject(error);
      });

      if (!isResponseOk(response, dispatch)) return;

      const result = (await response.json()) as { message: string };

      console.log(
        `Dispatched command ${sendToDivisionCommandInfo.CommandInfo.Command} to:
        Divisions: ${sendToDivisionCommandInfo.Divisions}`
      );

      dispatch({
        type: AssetActionTypes.NOTIFICATION_SEND_TO_DIVISION_SUCCESS_MESSAGE,
        message: result.message,
      });
    },

  sendScheduledToSiteNumberCommand:
    (
      sendScheduledToSiteNumberCommandInfo: SendToSiteNumbersScheduledCommandInfo
    ): AppThunkAction<KnownAction> =>
    async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      dispatch({
        type: AssetActionTypes.RESET_ALL_SENT_COMMAND_RESULT,
      });

      const response = await fetch(
        `CommandResponse/SendScheduledToSiteNumberCommand`,
        postRequestOptions(
          appState.userSessionReducer.sessionInfo?.Token,
          sendScheduledToSiteNumberCommandInfo
        )
      ).catch(error => {
        console.error(error);
        dispatch({
          type: AssetActionTypes.COMMAND_SEND_SUCCESS_MESSAGE,
          message: 'failed',
        });
        return Promise.reject(error);
      });

      if (!isResponseOk(response, dispatch)) {
        dispatch({
          type: AssetActionTypes.COMMAND_SEND_SUCCESS_MESSAGE,
          message: 'failed',
        });
        return;
      }

      const result = (await response.json()) as { message: string };

      console.log(
        `Dispatched command ${sendScheduledToSiteNumberCommandInfo.CommandInfo.Command} to:
        Stores:`,
        sendScheduledToSiteNumberCommandInfo.SiteNumbers
      );

      dispatch({
        type: AssetActionTypes.NOTIFICATION_SEND_TO_SITE_NUMBER_SUCCESS_MESSAGE,
        message: result.message,
      });
      dispatch({
        type: AssetActionTypes.COMMAND_SEND_SUCCESS_MESSAGE,
        message: 'success',
      });
    },

  sendScheduledToNodeGroupsCommand:
    (
      sendScheduledToNodeGroupsCommandInfo: SendToNodeGroupsScheduledCommandInfo
    ): AppThunkAction<KnownAction> =>
    async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      dispatch({
        type: AssetActionTypes.RESET_ALL_SENT_COMMAND_RESULT,
      });

      const response = await fetch(
        `CommandResponse/SendScheduledToNodeGroupsCommand`,
        postRequestOptions(
          appState.userSessionReducer.sessionInfo?.Token,
          sendScheduledToNodeGroupsCommandInfo
        )
      ).catch(error => {
        console.error(error);
        dispatch({
          type: AssetActionTypes.COMMAND_SEND_SUCCESS_MESSAGE,
          message: 'failed',
        });
        return Promise.reject(error);
      });

      if (!isResponseOk(response, dispatch)) {
        dispatch({
          type: AssetActionTypes.COMMAND_SEND_SUCCESS_MESSAGE,
          message: 'failed',
        });
        return;
      }

      const result = (await response.json()) as { message: string };

      console.log(
        `Dispatched command ${sendScheduledToNodeGroupsCommandInfo.CommandInfo.Command} to:
        Store: ${sendScheduledToNodeGroupsCommandInfo.SiteNumber}
        Clusters:`,
        sendScheduledToNodeGroupsCommandInfo.NodeGroups
      );

      dispatch({
        type: AssetActionTypes.NOTIFICATION_SEND_TO_NODE_GROUPS_SUCCESS_MESSAGE,
        message: result.message,
      });
      dispatch({
        type: AssetActionTypes.COMMAND_SEND_SUCCESS_MESSAGE,
        message: 'success',
      });
    },

  sendScheduledToLanesCommand:
    (
      sendScheduledToLanesCommandInfo: SendToLanesScheduledCommandInfo
    ): AppThunkAction<KnownAction> =>
    async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      dispatch({
        type: AssetActionTypes.RESET_ALL_SENT_COMMAND_RESULT,
      });

      const response = await fetch(
        `CommandResponse/SendScheduledToLanesCommand`,
        postRequestOptions(
          appState.userSessionReducer.sessionInfo?.Token,
          sendScheduledToLanesCommandInfo
        )
      ).catch(error => {
        console.error(error);
        dispatch({
          type: AssetActionTypes.COMMAND_SEND_SUCCESS_MESSAGE,
          message: 'failed',
        });
        return Promise.reject(error);
      });

      if (!isResponseOk(response, dispatch)) {
        dispatch({
          type: AssetActionTypes.COMMAND_SEND_SUCCESS_MESSAGE,
          message: 'failed',
        });
        return;
      }

      const result = (await response.json()) as { message: string };

      console.log(
        `Dispatched command ${sendScheduledToLanesCommandInfo.CommandInfo.Command} to:
        Store: ${sendScheduledToLanesCommandInfo.SiteNumber}
        Cluster: ${sendScheduledToLanesCommandInfo.NodeGroup}
        Lanes: `,
        sendScheduledToLanesCommandInfo.LaneNumbers
      );

      dispatch({
        type: AssetActionTypes.NOTIFICATION_SEND_TO_LANES_SUCCESS_MESSAGE,
        message: result.message,
      });
      dispatch({
        type: AssetActionTypes.COMMAND_SEND_SUCCESS_MESSAGE,
        message: 'success',
      });
    },

  sendScheduledToDivisionCommand:
    (
      sendScheduledToDivisionCommandInfo: SendToDivisionsScheduledCommandInfo
    ): AppThunkAction<KnownAction> =>
    async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      dispatch({
        type: AssetActionTypes.RESET_ALL_SENT_COMMAND_RESULT,
      });

      const response = await fetch(
        `CommandResponse/SendScheduledToDivisionCommand`,
        postRequestOptions(
          appState.userSessionReducer.sessionInfo?.Token,
          sendScheduledToDivisionCommandInfo
        )
      ).catch(error => {
        console.error(error);
        dispatch({
          type: AssetActionTypes.COMMAND_SEND_SUCCESS_MESSAGE,
          message: 'failed',
        });
        return Promise.reject(error);
      });

      if (!isResponseOk(response, dispatch)) {
        dispatch({
          type: AssetActionTypes.COMMAND_SEND_SUCCESS_MESSAGE,
          message: 'failed',
        });
        return;
      }

      const result = (await response.json()) as { message: string };

      console.log(
        `Dispatched command ${sendScheduledToDivisionCommandInfo.CommandInfo.Command} to:
        Divisions: ${sendScheduledToDivisionCommandInfo.Divisions}`
      );

      dispatch({
        type: AssetActionTypes.NOTIFICATION_SEND_TO_DIVISION_SUCCESS_MESSAGE,
        message: result.message,
      });
      dispatch({
        type: AssetActionTypes.COMMAND_SEND_SUCCESS_MESSAGE,
        message: 'success',
      });
    },

  clearSuccessMessage:
      (): AppThunkAction<KnownAction> => async (dispatch, getState) => {
        const appState = getState();
        if (!appState) return;
  
        console.log(
          `Dispatching ${AssetActionTypes.COMMAND_SEND_SUCCESS_MESSAGE}`
        );
        dispatch({
          type: AssetActionTypes.COMMAND_SEND_SUCCESS_MESSAGE,
          message: '',
        });
    },

  resetAllSendCommandResult:
    (): AppThunkAction<KnownAction> => async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      dispatch({
        type: AssetActionTypes.RESET_ALL_SENT_COMMAND_RESULT,
      });

      console.log(
        `Dispatched command  ${AssetActionTypes.RESET_ALL_SENT_COMMAND_RESULT}`
      );
    },
};

const initialNodeTypeCounts: NodeTypeCounts = {
  Robots: 0,
  MasterRobots: 0,
  Cashiers: 0,
  MobileAttendants: 0,
};

const initialNodesInfoByPage: NodeQueryResult = {
  PageNumber: 0,
  PageSize: 0,
  TotalCount: 0,
  TotalRows: 0,
  Result: [],
};

// ----------------
// REDUCER - For a given state and action, returns the new state. To support time travel, this must not mutate the old state.
export const unloadedState: AssetsState = {
  unauthorizedRequest: false,
  isFetchingNodes: false,
  storeCount: 0,
  nodeTypeCounts: initialNodeTypeCounts,
  isFetchingCategoryCounts: false,
  categoryCounts: [],
  isFetchingNodeAssetsInfo: false,
  nodeAssetsInfo: [],
  filteredAssetsData: [],
  nodesByPage: initialNodesInfoByPage,
  siteNumbers: [],
  isFetchingAssetCategories: false,
  assetCategories: [],
  isFetchingAssetNames: false,
  assetNames: new Map(),
  divisions: [],
  nodeTypes: [],
  clusters: [],
  laneNumbers: [],
  assetValues: new Map(),
  fetchError: { StatusCode: 0, Message: '' },
  isFetchingCategoryCountsOverview: false,
  categoryCountsOverview: [],
  filteredTickertsData: [],
  siteNumberByDivision: [],
  nodeGroupByDivisionAndSiteNumber: [],
  laneNumberByDivisionSiteNumberAndCluster: [],
  notificationSendToSiteNumberSuccessMessage: '',
  notificationSendToNodeGroupsSuccessMessage: '',
  notificationSendToLanesSuccessMessage: '',
  notificationSendToDivisionSuccessMessage: '',
  commandSuccessMessage: '',
};

export const reducer: Reducer<AssetsState> = (
  state: AssetsState | undefined,
  incomingAction: Action
): AssetsState => {
  if (state === undefined) {
    return unloadedState;
  }

  const action = incomingAction as KnownAction;
  switch (action.type) {
    case AssetActionTypes.RESET_STATE:
      return unloadedState;

    case AssetActionTypes.UNAUTHORIZED_REQUEST:
      return {
        ...state,
        unauthorizedRequest: true,
      };
    case AssetActionTypes.RECEIVED_STORE_COUNT:
      return {
        ...state,
        storeCount: action.count,
      };
    case AssetActionTypes.RECEIVED_NODE_TYPE_COUNTS:
      return {
        ...state,
        nodeTypeCounts: action.counts,
      };
    case AssetActionTypes.REQUEST_CATEGORY_COUNT:
      return {
        ...state,
        isFetchingCategoryCounts: action.refreshing,
      };
    case AssetActionTypes.RECEIVED_CATEGORY_COUNT:
      let newAssetValues = new Map();
      let assetValues = [];

      assetValues = action.counts.map(asset => asset.Value);
      newAssetValues.set(action.categoryCountRequest, assetValues);

      return {
        ...state,
        categoryCounts: action.counts,
        assetValues: newAssetValues,
        isFetchingCategoryCounts: false,
      };

    case AssetActionTypes.REQUEST_SYSTEM_TYPE_COUNT:
      return {
        ...state,
        isFetchingCategoryCountsOverview: action.refreshing,
      };
    case AssetActionTypes.RECEIVED_SYSTEM_TYPE_COUNT:
      return {
        ...state,
        categoryCountsOverview: action.counts,
        isFetchingCategoryCountsOverview: false,
      };

    case AssetActionTypes.REQUEST_NODE_ASSETS_INFO:
      return {
        ...state,
        isFetchingNodeAssetsInfo: true,
      };
    case AssetActionTypes.RECEIVED_NODE_ASSETS_INFO:
      let newNodesByPage = { ...state.nodesByPage };

      let nodeIndex = newNodesByPage.Result.findIndex(
        ({ NodeID }) => NodeID === action.nodeId
      );

      newNodesByPage.Result[nodeIndex].AssetInfoList = action.nodeAssetsInfo;
      return {
        ...state,
        nodesByPage: newNodesByPage,
        isFetchingNodeAssetsInfo: false,
        nodeAssetsInfo: action.nodeAssetsInfo,
      };
    case AssetActionTypes.EXPORT_FILTERED_ASSETS:
      return {
        ...state,
        filteredAssetsData: action.filteredNodesWithAsset,
      };
    case AssetActionTypes.REQUEST_NODES_BY_PAGE:
      return {
        ...state,
        isFetchingNodes: true,
      };
    case AssetActionTypes.RECEIVED_NODES_BY_PAGE:
      let currentNodesByPageResult = state.nodesByPage.Result;
      let updatedNodesByPage = action.nodesByPage;

      let totalResult = currentNodesByPageResult.concat(
        ...updatedNodesByPage.Result
      );

      updatedNodesByPage.Result = totalResult;

      return {
        ...state,
        isFetchingNodes: false,
        nodesByPage: updatedNodesByPage,
      };
    case AssetActionTypes.REQUEST_ALL_NODES:
      return {
        ...state,
        isFetchingNodes: true,
      };
    case AssetActionTypes.RECEIVED_ALL_NODES:
      return {
        ...state,
        isFetchingNodes: false,
        nodesByPage: action.nodesByPage,
      };
    case AssetActionTypes.RESET_NODES_RESULT:
      let resetNodesByPageResult = state.nodesByPage;
      resetNodesByPageResult.Result = [];

      return {
        ...state,
        nodesByPage: resetNodesByPageResult,
        filteredAssetsData: [],
      };
    case AssetActionTypes.RECEIVED_ASSET_STORE_LIST:
      return {
        ...state,
        siteNumbers: action.siteNumbers,
      };
    case AssetActionTypes.REQUEST_ASSET_CATEGORY_LIST:
      return {
        ...state,
        isFetchingAssetCategories: action.refreshing,
      };
    case AssetActionTypes.RECEIVED_ASSET_CATEGORY_LIST:
      return {
        ...state,
        assetCategories: action.assetCategories,
        isFetchingAssetCategories: false,
      };
    case AssetActionTypes.REQUEST_ASSET_NAME_LIST:
      return {
        ...state,
        isFetchingAssetNames: action.refreshing,
      };
    case AssetActionTypes.RECEIVED_ASSET_NAME_LIST:
      let newAssetNames = new Map(state.assetNames);

      if (action.assetNames != null && action.category != undefined) {
        newAssetNames.set(action.category, action.assetNames);
      }

      return {
        ...state,
        assetNames: newAssetNames,
        isFetchingAssetNames: false,
      };
    case AssetActionTypes.RECEIVED_DIVISION_LIST:
      return {
        ...state,
        divisions: action.divisions,
      };
    case AssetActionTypes.RECEIVED_NODETYPE_LIST:
      return {
        ...state,
        nodeTypes: action.nodeTypes,
      };
    case AssetActionTypes.RESET_FILTERED_ASSETS:
      return {
        ...state,
        filteredAssetsData: [],
      };
    case AssetActionTypes.RECEIVED_LANE_NUMBER_LIST:
      return {
        ...state,
        laneNumbers: action.laneNumbers,
      };
    case AssetActionTypes.RECEIVED_CLUSTER_LIST:
      return {
        ...state,
        clusters: action.clusters,
      };
    case AssetActionTypes.RECEIVED_HTTP_RESPONSE_ERROR_CODE:
      let fetchErrorData = {
        StatusCode: action.statusCode,
        Message: 'FETCH_ERROR_MESSAGE_ASSETS',
      };
      return {
        ...state,
        isFetchingNodes: false,
        fetchError: fetchErrorData,
        isFetchingAssetCategories: false,
        isFetchingAssetNames: false,
        isFetchingCategoryCounts: false,
      };
    case AssetActionTypes.RESET_HTTP_RESPONSE_FETCH_ERROR:
      let resetData = { StatusCode: 0, Message: '' };
      return {
        ...state,
        fetchError: resetData,
      };
    case AssetActionTypes.RECEIVED_SITENUMBER_BY_DIVISION:
      return {
        ...state,
        siteNumberByDivision: action.siteNumberByDivision,
      };
    case AssetActionTypes.RECEIVED_NODE_GROUP_BY_DIVISION_AND_SITENUMBER:
      return {
        ...state,
        nodeGroupByDivisionAndSiteNumber:
          action.nodeGroupByDivisionAndSiteNumber,
      };
    case AssetActionTypes.RECEIVED_LANE_NUMBERS_BY_DIVISION_SITENUMBER_AND_CLUSTER:
      return {
        ...state,
        laneNumberByDivisionSiteNumberAndCluster:
          action.laneNumberByDivisionSiteNumberAndCluster,
      };
    case AssetActionTypes.NOTIFICATION_SEND_TO_SITE_NUMBER_SUCCESS_MESSAGE:
      return {
        ...state,
        notificationSendToSiteNumberSuccessMessage: action.message,
      };
    case AssetActionTypes.NOTIFICATION_SEND_TO_NODE_GROUPS_SUCCESS_MESSAGE:
      return {
        ...state,
        notificationSendToNodeGroupsSuccessMessage: action.message,
      };
    case AssetActionTypes.NOTIFICATION_SEND_TO_LANES_SUCCESS_MESSAGE:
      return {
        ...state,
        notificationSendToLanesSuccessMessage: action.message,
      };
    case AssetActionTypes.NOTIFICATION_SEND_TO_DIVISION_SUCCESS_MESSAGE:
      return {
        ...state,
        notificationSendToDivisionSuccessMessage: action.message,
      };
    case AssetActionTypes.RESET_ALL_SENT_COMMAND_RESULT:
      return {
        ...state,
        notificationSendToSiteNumberSuccessMessage: '',
        notificationSendToNodeGroupsSuccessMessage: '',
        notificationSendToLanesSuccessMessage: '',
        notificationSendToDivisionSuccessMessage: '',
      };
    case AssetActionTypes.COMMAND_SEND_SUCCESS_MESSAGE:
      return {
        ...state,
        commandSuccessMessage: action.message,
      };

    default:
      break;
  }
  return state;
};
